<template>
  <div>
    <table class="table table-hover">
      <tbody>
        <div
          v-for="(survey, index) in paginatedSurveys"
          :key="survey.isDummyRow ? `dummy-${index}` : survey.name"
          :class="
            survey.isDummyRow
              ? 'dummy-row'
              : 'surveys-list-row table-row d-flex flex-wrap cursor-pointer mb-3 mb-sm-0'
          "
          @click="!survey.isDummyRow && selectSurvey(survey)"
        >
          <template v-if="survey.isDummyRow">
            <div class="surveys-list-date"></div>
            <div style="height: 1.5rem"></div>
          </template>
          <template v-else>
            <div
              class="w-100 d-flex justify-content-start column-gap-5 row-gap-2 flex-wrap-reverse"
            >
              <div class="surveys-list-date">
                <i class="bi bi-calendar3 me-2"></i>
                <span>{{ survey.details.start_date.split('T')[0] }} - </span>
                <span>{{ survey.details.end_date.split('T')[0] }}</span>
              </div>
              <div class="surveys-list-title">
                <span class="fw-bold">
                  {{ $tr(survey.details.messages, 'title') }}
                </span>
                <span
                  v-if="survey.details.status === 'published'"
                  class="badge badge-info ms-5"
                >
                  <i class="bi bi-arrow-repeat fs-5 text-white me-2"></i>
                  {{ $t('results.published') }}
                </span>
                <span
                  v-if="survey.details.status === 'aggregate'"
                  class="badge badge-info ms-5"
                >
                  {{ $t('aggregates.aggregated') }}
                </span>
              </div>
            </div>
          </template>
        </div>
      </tbody>
    </table>

    <!-- Pagination Controls -->
    <div v-if="totalPages > 1" class="pagination mt-10">
      <button
        class="pagination-button"
        :disabled="currentPage === 1"
        aria-label="Previous Page"
        @click="currentPage--"
      >
        <i class="bi bi-arrow-left"></i>
      </button>
      <span>{{ currentPage }} / {{ totalPages }}</span>
      <button
        class="pagination-button"
        :disabled="currentPage === totalPages"
        aria-label="Next Page"
        @click="currentPage++"
      >
        <i class="bi bi-arrow-right"></i>
      </button>
    </div>
    <div
      v-if="totalPages === 1 || currentPage === totalPages"
      class="d-flex justify-content-center"
    >
      <button
        v-if="!hasLoadedOlderSurveys"
        class="btn btn-sm btn-secondary mt-5"
        @click="loadAllHistoricalSurveys"
      >
        {{ $t('groupsComparison.loadMore') }}
      </button>
    </div>
  </div>
</template>

<script>
import { useResultsStore } from '@/stores/results-store'
import { useWidgetStore } from '@/stores/widget-store'
import { useInsightsStore } from '@/stores/insights-store'
import { tabTypes } from '@/constants/tab-types'

export default {
  name: 'SurveysList',
  props: {
    surveys: {
      type: Array,
      required: true
    },
    loadAllHistoricalSurveys: {
      type: Function,
      required: true
    },
    hasLoadedOlderSurveys: {
      type: Boolean,
      required: true
    }
  },
  setup() {
    const resultsStore = useResultsStore()
    const widgetStore = useWidgetStore()
    const insightsStore = useInsightsStore()
    return { insightsStore, widgetStore, resultsStore }
  },
  data() {
    return {
      currentPage: 1,
      itemsPerPage: 10
    }
  },
  computed: {
    totalPages() {
      // total pages is still based on the real number of surveys
      return Math.ceil(this.surveys.length / this.itemsPerPage)
    },
    paginatedSurveys() {
      const start = (this.currentPage - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      const pageItems = this.surveys.slice(start, end)

      // If we've sliced fewer than itemsPerPage, pad with dummy objects
      const padCount = this.itemsPerPage - pageItems.length
      if (this.totalPages > 1) {
        for (let i = 0; i < padCount; i++) {
          pageItems.push({
            isDummyRow: true,
            name: `dummy-${start + pageItems.length + i}`,
            details: {
              end_date: '',
              messages: { title: '' }
            }
          })
        }
      }
      return pageItems
    }
  },
  methods: {
    selectSurvey(survey) {
      // Don’t do anything if it’s a dummy
      if (survey.isDummyRow) return

      this.insightsStore.clear()
      this.resultsStore.clear()
      this.widgetStore.clear()
      this.$router.push({
        name: tabTypes.RESULTS,
        params: { surveyName: survey.name }
      })
    }
  }
}
</script>

<style scoped>
.table-row {
  border-radius: var(--bs-border-radius-lg) !important;
}
.table-row:hover {
  background-color: var(--kt-gray-100); /* Light gray for hover effect */
  transition: background-color 0.2s ease;
  border-radius: 10px;
}

/* Style dummy rows so they appear blank/unselectable */
.dummy-row {
  cursor: default;
  background-color: transparent !important;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
}
.pagination-button {
  width: 30px;
  height: 30px;
  padding: 5px 5px;
  border: 1px solid var(--kt-border-color);
  background-color: var(--kt-gray-100);
  cursor: pointer;
  border-radius: 25px;
}
.pagination-button:hover {
  background-color: var(--kt-gray-200);
}
.pagination-button:disabled {
  background-color: var(--kt-gray-300);
  cursor: not-allowed;
  opacity: var(--kt-btn-disabled-opacity);
}
.dummy-row {
  min-height: 36px;
}
@media (max-width: 576px) {
  .surveys-list-date {
    width: 100%;
  }
}
</style>
